import React, { useState, useEffect } from "react";
import { Box } from "@chakra-ui/react";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto } from "build-ts/schedule";
import { getScheduledJob, putScheduledJob } from "services/ScheduleServices";
import showToast from "hooks/useCustomToast";
import WorkflowDefinition from "modules/analyst/components/WorkflowDefinition";

interface WorkflowDefinitionContainerProps {
  jobName: string;
  setJobName: (value: string) => void;
  selectedJob?: Header;
  /** Pre-fetched full proto — skips the getScheduledJob call when provided. */
  initialJobDetails?: ScheduledJobProto;
  /** Fires when the user confirms a name edit — triggers a name-only API save. */
  triggerNameSave?: boolean;
  setTriggerNameSave?: (value: boolean) => void;
  setDarwinName: (value: string) => void;
  onViewExecutionLog?: () => void;
}

const WorkflowDefinitionContainer: React.FC<WorkflowDefinitionContainerProps> = ({
  jobName,
  setJobName,
  selectedJob,
  initialJobDetails,
  triggerNameSave = false,
  setTriggerNameSave,
  setDarwinName,
  onViewExecutionLog,
}) => {
  const [trigger, setTrigger] = useState<string>("");
  const [analysisInstructions, setAnalysisInstructions] = useState<string>("");
  const [reportingInstructions, setReportingInstructions] = useState<string>("");
  const [selectedJobDetails, setSelectedJobDetails] = useState<ScheduledJobProto | undefined>(undefined);

  // Schedule state kept in sync with CronBuilder
  const [isActive, setIsActive] = useState<boolean>(false);
  const [cronExpression, setCronExpression] = useState<string>("");

  const fetchJobDetails = async (selectedJobId: string) => {
    const response = await getScheduledJob(selectedJobId, false);
    if (response) {
      setSelectedJobDetails(response);
      setJobName(response.header?.name || "");
      setTrigger(response.scheduled_analysis?.trigger || "");
      setAnalysisInstructions(response.scheduled_analysis?.analysis_instructions || "");
      setReportingInstructions(response.scheduled_analysis?.reporting_instructions || "");
      setCronExpression(response.cron_expr || "");
      setIsActive(response.is_active || false);
      setDarwinName(response.scheduled_analysis?.darwin_name || "");
    }
  };

  // Name-only save — updates just the header name, preserving all other job fields
  const handleSaveNameOnly = async () => {
    if (!selectedJobDetails?.header?.id) return;
    if (!jobName.trim()) {
      showToast("Workflow name cannot be empty", "", "warning");
      return;
    }
    const job = ScheduledJobProto.create({
      ...selectedJobDetails,
      header: Header.create({
        ...selectedJobDetails.header,
        name: jobName.trim(),
      }),
    });
    const response = await putScheduledJob(job);
    if (response) {
      setSelectedJobDetails(response);
      showToast("Workflow name updated", "", "success");
    } else {
      showToast("Failed to update workflow name", "", "error");
    }
  };

  // Watch for triggerNameSave — fires when user confirms a name edit on an existing job
  useEffect(() => {
    if (triggerNameSave) {
      handleSaveNameOnly();
      setTriggerNameSave?.(false);
    }
  }, [triggerNameSave]);

  // Fetch job details from API if not provided in initialJobDetails
  useEffect(() => {
    if (selectedJob?.id) {
      if (initialJobDetails && initialJobDetails.header?.id === selectedJob.id) {
        setSelectedJobDetails(initialJobDetails);
        setJobName(initialJobDetails.header?.name || "");
        setTrigger(initialJobDetails.scheduled_analysis?.trigger || "");
        setAnalysisInstructions(initialJobDetails.scheduled_analysis?.analysis_instructions || "");
        setReportingInstructions(initialJobDetails.scheduled_analysis?.reporting_instructions || "");
        setCronExpression(initialJobDetails.cron_expr || "");
        setIsActive(initialJobDetails.is_active || false);
        setDarwinName(initialJobDetails.scheduled_analysis?.darwin_name || "");
      } else {
        fetchJobDetails(selectedJob.id);
      }
    } else {
      setJobName("");
      setTrigger("");
      setAnalysisInstructions("");
      setReportingInstructions("");
      setCronExpression("");
      setIsActive(false);
      setSelectedJobDetails(undefined);
    }
  }, [selectedJob?.id]);

  return (
    <Box flex="1" minH={0} minW={0} display="flex" flexDirection="column" overflow="hidden">
      <WorkflowDefinition
        trigger={trigger}
        analysisInstructions={analysisInstructions}
        reportingInstructions={reportingInstructions}
        cronExpression={cronExpression}
        setCronExpression={setCronExpression}
        isActive={isActive}
        setIsActive={setIsActive}
        selectedJobDetails={selectedJobDetails}
        onViewExecutionLog={onViewExecutionLog}
      />
    </Box>
  );
};

export default WorkflowDefinitionContainer;
